import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import {
  toMcpApiErrorMessage,
  useLazyListMcpThreadsQuery
} from '../api/mcpApi';
import type { McpThreadSummary } from '../types/mcpApiTypes';
import {
  getMcpThreadHistoryVersion,
  subscribeMcpThreadHistory
} from '../utils/threadHistoryEvents';

const PAGE_SIZE = 20;

export function useMcpThreadHistory(
  enabled: boolean,
  searchQuery = '',
  pageSize = PAGE_SIZE
) {
  const [items, setItems] = useState<McpThreadSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);
  const q = searchQuery.trim();
  const limit = pageSize > 0 ? pageSize : PAGE_SIZE;
  const [fetchThreads] = useLazyListMcpThreadsQuery();
  const historyVersion = useSyncExternalStore(
    subscribeMcpThreadHistory,
    getMcpThreadHistoryVersion,
    getMcpThreadHistoryVersion
  );

  const loadInitial = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchThreads({
        limit,
        offset: 0,
        q: q || undefined
      }).unwrap();
      setItems((res.items ?? []).slice(0, limit));
      setTotal(res.total ?? 0);
      setOffset(res.items?.length ?? 0);
      setHasMore(Boolean(res.hasMore));
    } catch (err) {
      setError(toMcpApiErrorMessage(err, 'خطا در دریافت سابقه'));
      setItems([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [fetchThreads, q, limit]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingRef.current) return;
    loadingRef.current = true;
    setLoadingMore(true);
    try {
      const res = await fetchThreads({
        limit,
        offset,
        q: q || undefined
      }).unwrap();
      setItems(prev => {
        const seen = new Set(prev.map(item => item.id));
        const next = (res.items ?? []).filter(item => !seen.has(item.id));
        return [...prev, ...next];
      });
      setOffset(prev => prev + (res.items?.length ?? 0));
      setHasMore(Boolean(res.hasMore));
      if (typeof res.total === 'number') {
        setTotal(res.total);
      }
    } catch (err) {
      setError(toMcpApiErrorMessage(err, 'خطا در بارگذاری بیشتر'));
    } finally {
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [fetchThreads, hasMore, offset, q, limit]);

  useEffect(() => {
    if (!enabled) return;
    void loadInitial();
  }, [enabled, loadInitial, historyVersion]);

  return {
    items,
    total,
    loading,
    loadingMore,
    hasMore,
    error,
    reload: loadInitial,
    loadMore
  };
}
